/**
 * Opportunity Engine V3 - Entry Point
 * Uses global master dataset as lookup table for CAPEX pricing
 * 3-Step Wizard: Dataset → Match & Calculate → Portfolio & MACC
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Database, BarChart3, Target, Brain
} from 'lucide-react';
import OpportunityDatasetReviewV3 from './OpportunityDatasetReviewV3';
import OpportunityMatchingV3 from './OpportunityMatchingV3';
import OpportunityPortfolioV3 from './OpportunityPortfolioV3';

const API = process.env.REACT_APP_BACKEND_URL;

const OpportunityEngineV3 = ({ study }) => {
  const [masterDataset, setMasterDataset] = useState(null);
  const [seuData, setSeuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState('dataset');

  useEffect(() => {
    if (study?.id) {
      fetchData();
    }
  }, [study?.id]);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      // Use site-level master dataset (falls back to company master on backend if empty)
      const siteId = study.site_id;
      const masterUrl = siteId
        ? `${API}/api/opportunity-v3/sites/${siteId}/master-dataset`
        : `${API}/api/opportunity-v3/master-dataset`;
      const masterRes = await axios.get(masterUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMasterDataset(masterRes.data);

      const seuRes = await axios.get(`${API}/api/opportunity-v3/studies/${study.id}/seus`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeuData(seuRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-gray-600">Loading Opportunity Engine...</p>
        </div>
      </div>
    );
  }

  // 3-Step Wizard
  const steps = [
    {
      id: 'dataset',
      label: 'Master Dataset',
      icon: Database,
      description: 'Global CAPEX pricing'
    },
    {
      id: 'match',
      label: 'Match & Calculate',
      icon: Target,
      description: 'SEU → Opportunities'
    },
    {
      id: 'portfolio',
      label: 'Portfolio & MACC',
      icon: BarChart3,
      description: 'Ranked + tCO₂e'
    }
  ];

  return (
    <div className="space-y-4">
      {/* Compact Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Database className="w-8 h-8 text-white/80" />
            <div>
              <h2 className="text-xl font-bold">Opportunity Engine V3</h2>
              <p className="text-indigo-100 text-sm">SEU-driven opportunity matching with MACC analysis</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-right">
            <div>
              <div className="text-xs text-indigo-200">Master Dataset</div>
              <span className="font-bold text-green-300">{masterDataset?.total_items || 0} Items</span>
              {masterDataset?.filter_applied && (
                <div className="text-[11px] text-indigo-100 mt-0.5">
                  Industry: <span className="font-semibold capitalize">{(masterDataset?.industry || '').replace(/_/g, ' ')}</span>
                  {' — '}filtered {masterDataset?.total_items_unfiltered} → {masterDataset?.total_items}
                </div>
              )}
            </div>
            <div className="h-8 w-px bg-indigo-400"></div>
            <div>
              <div className="text-xs text-indigo-200">Study SEUs</div>
              <span className="font-bold text-yellow-300">{seuData?.total_seus || 0} SEUs</span>
            </div>
          </div>
        </div>
      </div>

      {/* Step Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 p-2">
        <div className="flex items-center gap-1">
          {steps.map((step) => {
            const Icon = step.icon;
            const isActive = activeStep === step.id;
            
            return (
              <button
                key={step.id}
                onClick={() => setActiveStep(step.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition ${
                  isActive
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
                data-testid={`step-${step.id}`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{step.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div>
        {activeStep === 'dataset' && (
          <OpportunityDatasetReviewV3 
            study={study} 
            masterDataset={masterDataset}
            onRefresh={fetchData}
          />
        )}
        
        {activeStep === 'match' && (
          <OpportunityMatchingV3
            study={study}
            masterDataset={masterDataset}
            onRefresh={fetchData}
            onViewPortfolio={() => setActiveStep('portfolio')}
          />
        )}
        
        {activeStep === 'portfolio' && (
          <OpportunityPortfolioV3
            study={study}
            onBack={() => setActiveStep('match')}
          />
        )}
      </div>

      {/* Info Footer */}
      {activeStep === 'match' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-sm text-blue-800">
            <Brain className="w-4 h-4 text-blue-600" />
            <span>
              <strong>Workflow:</strong> Match SEUs → Calculate CAPEX & Savings → View Portfolio with MACC curves and tCO₂e reduction
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunityEngineV3;
